import React, { useState, useEffect, useCallback } from 'react';
import { parseJson } from '../../form_tools/extraFunction';
import {
  createDefaultCondition,
  dynamicRender,
  v2ConditionsToV1Statement,
} from '../../form_tools/dynamicRender';
import { parsePlaceholder } from '../../utils/detectAnswer';
import { IconButton } from '@mui/material';
import { KeyboardArrowUp, KeyboardArrowDown, Check } from '@mui/icons-material';
import { PrimaryButton } from '../common';

import CommentBubble from '../CommentBubble';
import './ConversationalLayout.css';

const isEmptyValue = (value) => {
  if (!value) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object') {
    if (value.text_answer === '' || value.text_answer === undefined)
      return true;
    if (value.number_answer === '' || value.number_answer === undefined)
      return true;
    if (
      value.choice_answer &&
      (!value.choice_answer.length || value.choice_answer[0] === '')
    )
      return true;
    if (value.calendar_answer === '' || value.calendar_answer === undefined)
      return true;
    if (value.answer && (!value.answer.length || value.answer[0] === ''))
      return true;
  }
  return false;
};

const parseAddressAuto = value => {
  if (value && typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (e) {
      return {
        formatted: value,
        street_address: '',
        locality: '',
        region: '',
        postal_code: '',
        country: '',
      };
    }
  }
  return {
    formatted: '',
    street_address: '',
    locality: '',
    region: '',
    postal_code: '',
    country: '',
  };
};

const createAnswerData = (format, auto) => {
  switch (format) {
    case 'number':
    case 'number_input':
      return { number_answer: auto.toString() };
    case 'short_text':
    case 'single_line_text':
      return { text_answer: auto.toString() };
    case 'long_text':
    case 'free_text':
      return { text_answer: auto.toString() };
    case 'radio_button':
    case 'single_choice':
      return { choice_answer: [auto] };
    case 'checkbox':
    case 'multiple_choice':
      return { answer: [auto] };
    case 'dropdown':
    case 'single_select':
      return { choice_answer: [auto] };
    case 'date':
    case 'calendar':
      return { calendar_answer: auto };
    case 'map':
      return { map_answer: auto };
    case 'list':
    case 'list_input':
      const listItems = Array.isArray(auto) ? auto : [auto];
      return {
        answer: listItems.map((item) =>
          typeof item === 'object' ? JSON.stringify(item) : String(item)
        ),
      };
    case 'signature':
    case 'esignature':
      return { esignature_answer: auto };
    case 'image_annotation':
      return { image_annotation_answer: auto };
    case 'address':
      return { address_answer: parseAddressAuto(auto) };
    default:
      return {
        answer: [
          typeof auto === 'object' ? JSON.stringify(auto) : String(auto),
        ],
      };
  }
};

const ConversationalRenderer = ({
  pageData,
  form,
  formInstance,
  componentRenderer,
  readOnly,
  timezone,
  requiredList,
  requiredListTable,
  username,
  sended,
  pageProcessing,
  previewMode,
  onAnswerChange,
  onSubmit,
  onNext,
  onBack,
  onGoToPage,
  currentPageIndex,
  totalPages,
  pageTitles,
  isLastPage,
  canSubmitSubmission,
  hasBeenSubmitted,
  isSubmitting,
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [direction, setDirection] = useState('next');
  const [visibleComponents, setVisibleComponents] = useState([]);
  const [showRequiredError, setShowRequiredError] = useState(false);

  const isV2Component = (comp) => comp.type && !comp.views;

  const normalizeV2Columns = (columns) => {
    if (!Array.isArray(columns)) return columns;
    return columns.map(col => ({
      ...col,
      columnName: col.columnName || col.label || '',
      id: col.id || col.key || '',
      question: col.question || col.label || '',
    }));
  };

  const normalizeV2ExportType = (exportType) => {
    if (exportType === 'columns_as_lists') return 'columnsAsLists';
    if (exportType === 'rows_as_lists') return 'rowsAsLists';
    return exportType;
  };

  // Process all components and filter visible ones
  const processComponent = useCallback(
    (component) => {
      const fieldType = component.type || component.views?.answer?.view?.format;
      const v2 = isV2Component(component);

      if (v2) {
        if (component.visible && component.visible.conditions) {
          component = { ...component, visibility: v2ConditionsToV1Statement(component.visible) };
        }
        if (component.required && component.required.conditions) {
          component = { ...component, required: v2ConditionsToV1Statement(component.required) };
        }
        if (component.readOnly && component.readOnly.conditions) {
          component = { ...component, read_only: v2ConditionsToV1Statement(component.readOnly), readOnly: undefined };
        }
        if (component.prefill && typeof component.prefill === 'object' && component.prefill.conditions) {
          component = { ...component, auto: v2ConditionsToV1Statement(component.prefill), prefill: undefined };
        }
        if (component.comment && typeof component.comment === 'object' && component.comment.conditions) {
          component = { ...component, comment: v2ConditionsToV1Statement(component.comment) };
        }
      }

      let questionText;
      let answerList;
      let questionList;

      if (v2) {
        questionText = component.label !== undefined ? component.label : '';
        if (fieldType === 'table' || fieldType === 'repeatable_section') {
          answerList = normalizeV2Columns(component.columns || component.fields || []);
        } else {
          answerList = component.options || '';
        }
        questionList = questionText;
      } else {
        questionText = component.label || component.views?.question?.view?.data?.default;
        answerList = parseJson(
          dynamicRender(
            formInstance,
            component.views?.answer?.view?.data?.default,
            component.views?.answer?.view?.data?.dynamic
          )
        );
        questionList = parseJson(
          dynamicRender(
            formInstance,
            questionText,
            component.views?.question?.view?.data?.dynamic
          )
        );
      }

      let visible;
      if (component.visibility !== undefined && component.visibility !== null) {
        createDefaultCondition(component.visibility, form);
        visible = dynamicRender(formInstance, '', component.visibility) + '';
      } else {
        visible = component.visible === false ? 'false' : 'true';
      }

      let auto = (v2 && !component.auto)
        ? (component.prefill || '')
        : dynamicRender(formInstance, '', component.auto);
      if (auto !== '') {
        if (
          fieldType === 'multiple_choice' ||
          fieldType === 'single_choice' ||
          fieldType === 'single_select' ||
          fieldType === 'checkbox' ||
          fieldType === 'radio_button' ||
          fieldType === 'dropdown'
        ) {
          const answer = formInstance.isContain
            ? formInstance.isContain(auto, answerList)
            : null;

          if (!answer) {
            auto = '';
          } else {
            if (fieldType === 'single_select' || fieldType === 'dropdown') {
              const value = Array.isArray(answer) ? answer[1] : answer;
              auto = [null, value];
            } else {
              auto = answer;
            }
          }
        } else if (typeof answerList !== 'object') {
          answerList = auto;
        }
      }

      const readonly = (v2 && !component.read_only)
        ? (component.readOnly === true ? 'true' : 'false')
        : dynamicRender(formInstance, 'false', component.read_only) + '';

      if (visible === 'false' || visible === '') {
        if (
          auto !== '' &&
          onAnswerChange &&
          (!form.answer_data[component.key] ||
            isEmptyValue(form.answer_data[component.key]))
        ) {
          const answer_data = (() => {
            switch (fieldType) {
              case 'number':
              case 'number_input':
                return { number_answer: auto.toString() };
              case 'short_text':
              case 'single_line_text':
                return { text_answer: auto.toString() };
              case 'long_text':
              case 'free_text':
                return { text_answer: auto.toString() };
              case 'radio_button':
              case 'single_choice':
              case 'checkbox':
              case 'multiple_choice':
              case 'dropdown':
              case 'single_select':
                return { choice_answer: [auto] };
              case 'date':
              case 'calendar':
                return { calendar_answer: auto };
              case 'address':
                return { address_answer: parseAddressAuto(auto) };
              default:
                return { answer: [auto] };
            }
          })();

          setTimeout(() => {
            onAnswerChange(
              answer_data,
              component.key,
              questionText,
              auto
            );
          }, 0);
        }
        return null;
      }

      return {
        component,
        answerList,
        questionList,
        auto,
        readonly,
        visible: true,
      };
    },
    [form, formInstance, onAnswerChange]
  );

  // Track page changes to reset question index only on actual page navigation
  const [lastPageIndex, setLastPageIndex] = useState(currentPageIndex);

  const pageComponents = pageData?.components || pageData?.fields;

  // Update visible components when form data changes
  useEffect(() => {
    if (!pageData || !pageComponents) {
      setVisibleComponents([]);
      return;
    }

    const processed = pageComponents
      .map(processComponent)
      .filter((c) => c !== null);
    setVisibleComponents(processed);
  }, [pageData, pageComponents, processComponent]);

  // Only reset question index when navigating to a different page
  useEffect(() => {
    if (currentPageIndex !== lastPageIndex) {
      setCurrentQuestionIndex(0);
      setLastPageIndex(currentPageIndex);
      setShowRequiredError(false); // Clear error when page changes
    }
  }, [currentPageIndex, lastPageIndex]);

  const totalQuestions = visibleComponents.length;
  const currentComponent = visibleComponents[currentQuestionIndex];

  // Validate if current question is required and has an answer
  const validateCurrentQuestion = useCallback(() => {
    if (!currentComponent) return true;

    const { component } = currentComponent;
    const isRequired = dynamicRender(formInstance, 'false', component.required);

    // If not required, validation passes
    if (!isRequired || isRequired === 'false') {
      setShowRequiredError(false);
      return true;
    }

    // Check if there's an answer for this component
    const answerData = form.answer_data[component.key];

    // Check various answer formats
    const hasAnswer = (() => {
      if (!answerData) return false;

      // Check standard answer array
      if (
        answerData.answer &&
        answerData.answer.length > 0 &&
        answerData.answer[0] !== null &&
        answerData.answer[0] !== ''
      ) {
        return true;
      }

      // Check text_answer
      if (
        answerData.text_answer !== undefined &&
        answerData.text_answer !== null &&
        answerData.text_answer !== ''
      ) {
        return true;
      }

      // Check number_answer
      if (
        answerData.number_answer !== undefined &&
        answerData.number_answer !== null &&
        answerData.number_answer !== ''
      ) {
        return true;
      }

      // Check choice_answer
      if (
        answerData.choice_answer &&
        answerData.choice_answer.length > 0 &&
        answerData.choice_answer[0] !== null &&
        answerData.choice_answer[0] !== ''
      ) {
        return true;
      }

      // Check calendar_answer
      if (
        answerData.calendar_answer !== undefined &&
        answerData.calendar_answer !== null &&
        answerData.calendar_answer !== ''
      ) {
        return true;
      }

      // Check map_answer
      if (
        answerData.map_answer !== undefined &&
        answerData.map_answer !== null &&
        answerData.map_answer !== ''
      ) {
        return true;
      }

      // Check esignature_answer
      if (
        answerData.esignature_answer !== undefined &&
        answerData.esignature_answer !== null &&
        answerData.esignature_answer !== ''
      ) {
        return true;
      }

      // Check image_annotation_answer
      if (
        answerData.image_annotation_answer !== undefined &&
        answerData.image_annotation_answer !== null &&
        answerData.image_annotation_answer !== ''
      ) {
        return true;
      }

      // Check address_answer
      if (
        answerData.address_answer &&
        (answerData.address_answer.formatted ||
          answerData.address_answer.street_address ||
          answerData.address_answer.locality ||
          answerData.address_answer.region ||
          answerData.address_answer.postal_code ||
          answerData.address_answer.country)
      ) {
        return true;
      }

      return false;
    })();

    if (!hasAnswer) {
      setShowRequiredError(true);
      return false;
    }

    setShowRequiredError(false);
    return true;
  }, [currentComponent, form.answer_data, formInstance]);

  // Clear error when answer changes for current question
  useEffect(() => {
    if (currentComponent && showRequiredError) {
      const answerData = form.answer_data[currentComponent.component.key];
      if (answerData) {
        // Re-validate silently when answer changes
        validateCurrentQuestion();
      }
    }
  }, [
    form.answer_data,
    currentComponent,
    showRequiredError,
    validateCurrentQuestion,
  ]);

  // Calculate overall progress across all pages
  const questionsBeforeThisPage = currentPageIndex * 10; // Estimate
  const overallProgress =
    totalPages > 1
      ? (currentPageIndex / totalPages) * 100 +
        (currentQuestionIndex / Math.max(totalQuestions, 1)) *
          (100 / totalPages)
      : ((currentQuestionIndex + 1) / Math.max(totalQuestions, 1)) * 100;

  const handleNextQuestion = useCallback(() => {
    // Validate current question before proceeding
    if (!validateCurrentQuestion()) {
      return; // Block navigation if validation fails
    }

    setShowRequiredError(false);

    if (currentQuestionIndex < totalQuestions - 1) {
      setDirection('next');
      setCurrentQuestionIndex((prev) => prev + 1);
    } else if (!isLastPage && onNext) {
      // Move to next page
      onNext();
    }
  }, [
    currentQuestionIndex,
    totalQuestions,
    isLastPage,
    onNext,
    validateCurrentQuestion,
  ]);

  const handlePrevQuestion = useCallback(() => {
    setShowRequiredError(false); // Clear error when going back
    if (currentQuestionIndex > 0) {
      setDirection('prev');
      setCurrentQuestionIndex((prev) => prev - 1);
    } else if (currentPageIndex > 0 && onBack) {
      // Move to previous page
      onBack();
    }
  }, [currentQuestionIndex, currentPageIndex, onBack]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeElement = document.activeElement;
      const isTextarea = activeElement && activeElement.tagName === 'TEXTAREA';
      const isTextInput =
        activeElement &&
        activeElement.tagName === 'INPUT' &&
        [
          'text',
          'email',
          'password',
          'search',
          'url',
          'tel',
          'number',
        ].includes(activeElement.type);

      // Arrow keys for navigation (unless in text input)
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        // Allow arrow keys in text inputs for cursor movement
        if (isTextInput || isTextarea) return;

        e.preventDefault();
        if (e.key === 'ArrowDown') {
          handleNextQuestion();
        } else {
          handlePrevQuestion();
        }
        return;
      }

      // Shift+Enter to advance (Enter alone is for new lines in text fields)
      if (e.key === 'Enter' && e.shiftKey) {

        e.preventDefault();

        // Blur any focused input so the value is committed
        if (activeElement && activeElement.tagName === 'INPUT') {
          activeElement.blur();
        }

        // Use setTimeout to ensure the blur event has time to update the answer
        setTimeout(() => {
          if (currentQuestionIndex === totalQuestions - 1 && isLastPage) {
            if (onSubmit && canSubmitSubmission) {
              onSubmit();
            }
          } else {
            handleNextQuestion();
          }
        }, 50);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    handleNextQuestion,
    handlePrevQuestion,
    currentQuestionIndex,
    totalQuestions,
    isLastPage,
    onSubmit,
    canSubmitSubmission,
  ]);

  const renderRequiredErrors = (componentKey) => {
    const hasTableErrors = requiredListTable[componentKey];
    const hasFieldError = requiredList.includes(componentKey);

    if (!hasTableErrors && !hasFieldError) return null;

    return (
      <div className="conv-error-container">
        {hasTableErrors &&
          hasTableErrors.map((colName, index) => (
            <div key={index} className="conv-error">
              <i className="material-icons">error</i>
              <span>{colName} is a required field</span>
            </div>
          ))}
        {hasFieldError && (
          <div className="conv-error">
            <i className="material-icons">error</i>
            <span>This question is required</span>
          </div>
        )}
      </div>
    );
  };

  const renderQuestion = (component, questionList) => {
    const fieldType = component.type || component.views?.answer?.view?.format;
    if (fieldType === 'row_text' || fieldType === 'content_block') {
      return null;
    }

    const v2 = isV2Component(component);
    const description = v2
      ? (component.dynamicDescription
          ? dynamicRender(formInstance, component.description || '', component.dynamicDescription.Statement ? component.dynamicDescription : null)
          : (component.description || ''))
      : component.views?.question?.description?.default;
    const isRequired = (() => {
      if (component.required === true) return true;
      if (component.required === false) return false;
      if (component.required && component.required.Statement) {
        return !!dynamicRender(formInstance, false, component.required);
      }
      if (v2) return false;
      return !!dynamicRender(formInstance, 'false', component.required);
    })();

    return (
      <div className="conv-question">
        <div className="conv-question-header">
          <span className="conv-question-number">
            {currentQuestionIndex + 1}
          </span>
          <span className="conv-question-arrow">→</span>
          <div className="conv-question-title">
            {componentRenderer.componentSelector(
              component.views?.question?.view?.format || 'row_text',
              {
                data: (questionList || '') + (isRequired ? ' *' : ''),
                style: v2 ? undefined : parseJson(component.views?.question?.style),
              },
              'question',
              component.key
            )}
          </div>
        </div>
        {description && (
          <p className="conv-question-description">
            {parsePlaceholder(
              v2
                ? (description || '')
                : dynamicRender(
                    formInstance,
                    description,
                    component.views?.question?.description?.dynamic
                  ),
              form
            )}
          </p>
        )}
      </div>
    );
  };

  const renderAnswer = (component, answerList, auto, readonly) => {
    const fieldType = component.type || component.views?.answer?.view?.format;
    const v2 = isV2Component(component);
    const questionText = v2
      ? (component.label !== undefined ? component.label : '')
      : (component.label || component.views?.question?.view?.data?.default);

    return (
      <div className="conv-answer">
        {componentRenderer.componentSelector(
          fieldType,
          {
            ...component,
            data: fieldType === 'file_preview'
              ? (component.fileUrl || component.rte_answer_data_default || component.views?.answer?.view?.data?.default || answerList || '')
              : (answerList != null ? answerList : ''),
            style: v2 ? undefined : parseJson(component.views?.answer?.style),
            key: component.key,
            comment: component.comment,
            list_min: component.minItems ?? component.list_min,
            list_max: component.maxItems ?? component.list_max,
            file_type: component.file_type ?? component.fileType,
            question_text: questionText,
            drop_down: component.drop_down ?? component.searchable,
            outputFormat: component.outputFormat,
            timezone: component.timezone,
            numRows: component.numRows ?? component.minRows,
            exportType: v2 ? normalizeV2ExportType(component.exportType) : component.exportType,
            tableCells: component.columns || component.tableCells,
            validation: component.validation,
            ...(component.views?.answer?.view?.data?.rowLabels && {
              rowLabels: component.views.answer.view.data.rowLabels,
            }),
            ...(v2 && component.rowLabels ? { rowLabels: component.rowLabels } : {}),
            auto: auto,
            read_only: readOnly === true ? true : JSON.parse(readonly),
          },
          'answer',
          component.key
        )}
      </div>
    );
  };

  const renderComments = (component) => {
    if (!component.comment) return null;

    return (
      <div className="conv-comments">
        <h6>Comments</h6>
        {form.answer_data[component.key] &&
          form.answer_data[component.key].comment && (
            <CommentBubble
              legacy
              comment={form.answer_data[component.key].comment}
            />
          )}
        {form.comments[component.key] &&
          form.comments[component.key].map(({ comment, user, date }) => (
            <CommentBubble
              key={`${component.key}-${date}`}
              comment={comment}
              user={user}
              date={date}
              timezone={timezone}
            />
          ))}
      </div>
    );
  };

  if (!pageData || !pageComponents || totalQuestions === 0) {
    return (
      <div className="conv-container">
        <div className="conv-empty">No questions on this page</div>
      </div>
    );
  }

  const { component, answerList, questionList, auto, readonly } =
    currentComponent;
  const isFirstQuestion = currentQuestionIndex === 0 && currentPageIndex === 0;
  const isLastQuestion =
    currentQuestionIndex === totalQuestions - 1 && isLastPage;
  // Always show submit button on last question of last page
  const showSubmitButton = isLastQuestion;

  // Handle page navigation click
  const handlePageClick = (pageIndex) => {
    if (onGoToPage && pageIndex !== currentPageIndex) {
      setCurrentQuestionIndex(0);
      onGoToPage(pageIndex);
    }
  };

  // Generate page markers for the progress bar
  const pageMarkers = [];
  if (totalPages > 1) {
    for (let i = 1; i < totalPages; i++) {
      const position = (i / totalPages) * 100;
      pageMarkers.push(
        <div
          key={`marker-${i}`}
          className={`conv-progress-marker ${currentPageIndex >= i ? 'completed' : ''}`}
          style={{ left: `${position}%` }}
          onClick={() => handlePageClick(i)}
          title={`Go to page ${i + 1}`}
        />
      );
    }
  }

  return (
    <div className="conv-container">
      {/* Progress bar with page markers */}
      <div className="conv-progress-container">
        <div className="conv-progress-wrapper">
          <div className="conv-progress-track">
            <div
              className="conv-progress-fill"
              style={{ width: `${overallProgress}%` }}
            />
            {pageMarkers}
          </div>
          {totalPages > 1 && pageTitles && (
            <div className="conv-progress-pages">
              {pageTitles.map((title, i) => (
                <div
                  key={`page-${i}`}
                  className={`conv-progress-page ${
                    i < currentPageIndex ? 'completed' : ''
                  } ${i === currentPageIndex ? 'active' : ''}`}
                  onClick={() => handlePageClick(i)}
                  title={title}
                >
                  <span className="conv-progress-page-num">{i + 1}</span>
                  <span className="conv-progress-page-title">{title}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Question content */}
      <div
        className={`conv-content conv-slide-${direction}`}
        key={component.key}
      >
        {/* Page title above each question */}
        {(pageData?.title || pageData?.title?.view?.data?.default) && (
          <div className="conv-page-title">
            {typeof pageData.title === 'string' ? pageData.title : pageData.title?.view?.data?.default}
          </div>
        )}
        {renderRequiredErrors(component.key)}
        {renderQuestion(component, questionList)}
        {renderAnswer(component, answerList, auto, readonly)}
        {showRequiredError && (
          <div className="conv-error-container conv-inline-error">
            <div className="conv-error">
              <i className="material-icons">error</i>
              <span>Please answer this question before continuing</span>
            </div>
          </div>
        )}
        {renderComments(component)}

        {/* Navigation hint */}
        <div className="conv-nav-hint">
          {showSubmitButton ? (
            <PrimaryButton
              onClick={onSubmit}
              disabled={
                readOnly ||
                previewMode ||
                isSubmitting ||
                (hasBeenSubmitted && !canSubmitSubmission)
              }
              startIcon={<Check />}
              className="conv-submit-btn"
            >
              {isSubmitting
                ? 'Submitting...'
                : hasBeenSubmitted
                  ? 'Resubmit'
                  : 'Submit'}
            </PrimaryButton>
          ) : (
            <PrimaryButton
              onClick={handleNextQuestion}
              disabled={pageProcessing}
              className="conv-next-btn"
            >
              OK
            </PrimaryButton>
          )}
          <span className="conv-keyboard-hint">
            press <strong>Shift + Enter ↵</strong>
          </span>
        </div>
      </div>

      {/* Navigation arrows */}
      <div className="conv-nav-arrows">
        <IconButton
          onClick={handlePrevQuestion}
          disabled={isFirstQuestion}
          className="conv-nav-arrow"
          size="small"
        >
          <KeyboardArrowUp />
        </IconButton>
        <IconButton
          onClick={handleNextQuestion}
          disabled={isLastQuestion}
          className="conv-nav-arrow"
          size="small"
        >
          <KeyboardArrowDown />
        </IconButton>
      </div>

      {/* Question counter */}
      <div className="conv-counter">
        {currentQuestionIndex + 1} of {totalQuestions}
        {totalPages > 1 && ` (Page ${currentPageIndex + 1} of ${totalPages})`}
      </div>
    </div>
  );
};

export default ConversationalRenderer;
